import {
  fromEvent,
  Observable,
  share,
  first,
  switchMap,
  of,
  merge,
  sampleTime,
  map,
  finalize,
  takeUntil,
  EMPTY,
} from 'rxjs';
import { Point } from '../helpers/coords/point';

export class MovingUtils {
  static observeMovingEvents(cfg: {
    elementToStartEvent?: Element;
    targetAreaMoveElement?: Element;
    moveObservable?: Observable<MouseEvent>;
    initialDelay?: number;
    startEventObservable?: Observable<MouseEvent>;
    stopEventObservable?: Observable<any>;
    onMoveStart?: Function;
    onMoveEnd?: Function;
    lockAxisX?: boolean;
    lockAxisY?: boolean;
  }): Observable<{ event: Event; stepPoint: Point }> {
    const initialDelay = cfg.initialDelay ?? -1;
    const onMoveStart = cfg.onMoveStart;
    const onMoveEnd = cfg.onMoveEnd;
    const lockAxisX = !!cfg.lockAxisX;
    const lockAxisY = !!cfg.lockAxisY;

    let initialX;
    let initialY;

    const moveObservable = (
      cfg.moveObservable ??
      merge(
        fromEvent<MouseEvent>(
          cfg.targetAreaMoveElement ?? window,
          'mousemove',
          {
            passive: true,
          }
        ),
        fromEvent<TouchEvent>(
          cfg.targetAreaMoveElement ?? window,
          'touchmove',
          {
            passive: true,
          }
        )
      )
    ).pipe(share());

    const stopEventObservable = (
      cfg.stopEventObservable ??
      merge(
        fromEvent<MouseEvent>(window, 'mouseup', {
          passive: true,
        }),
        fromEvent<TouchEvent>(window, 'touchend', {
          passive: true,
        }),
        fromEvent<TouchEvent>(window, 'touchcancel', {
          passive: true,
        })
      )
    ).pipe(first());

    const startEventObservable =
      cfg.startEventObservable ??
      merge(
        fromEvent<MouseEvent>(cfg.elementToStartEvent, 'mousedown', {
          passive: true,
        }),
        fromEvent<TouchEvent>(cfg.elementToStartEvent, 'touchstart', {
          passive: true,
        })
      );

    return startEventObservable.pipe(
      switchMap((e) => {
        onMoveStart?.(e);

        if (e?.defaultPrevented) {
          onMoveEnd?.();
          return EMPTY;
        }

        let observableAux =
          initialDelay < 0
            ? of(e)
            : merge(of(e), moveObservable).pipe(
                sampleTime(initialDelay),
                first()
              );

        return observableAux.pipe(
          switchMap((e) => {
            initialX = extractPos(e, 'clientX');
            initialY = extractPos(e, 'clientY');

            return moveObservable.pipe(
              map((e) => {
                const x = lockAxisX ? 0 : extractPos(e, 'clientX') - initialX;
                const y = lockAxisY ? 0 : extractPos(e, 'clientY') - initialY;

                return {
                  event: e,
                  stepPoint: new Point(x, y),
                };
              })
            );
          }),
          finalize(() => {
            onMoveEnd?.();
          }),
          takeUntil(stopEventObservable)
        );
      })
    );
  }
}

function extractPos(e: MouseEvent | TouchEvent, property: string): number {
  return (e as MouseEvent)[property] ?? (e as TouchEvent).touches[0][property];
}
